import { useEffect, useState, useCallback } from "react";
import { ScrollText, Plus, X, Trash2, Pencil } from "lucide-react";
import api from "../../lib/api";
import { errText } from "../../lib/errors";
import { toast } from "sonner";

const tl = (n) => (n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inputCls = "h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30 w-full";
const today = new Date().toISOString().slice(0, 10);
const STATUSES = [["portfoy", "Portföyde"], ["odendi", "Ödendi"], ["ciro", "Ciro Edildi"], ["protestolu", "Protestolu"], ["iade", "İade"]];
const STATUS_CLS = { portfoy: "bg-amber-100 text-amber-800", odendi: "bg-emerald-100 text-emerald-800", ciro: "bg-sky-100 text-sky-800", protestolu: "bg-red-100 text-red-800", iade: "bg-stone-200 text-stone-600" };

export default function AdminChecks() {
  const [data, setData] = useState(null);
  const [caris, setCaris] = useState([]);
  const [dirFilter, setDirFilter] = useState("");
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (dirFilter) params.direction = dirFilter;
      const [{ data: d }, { data: cl }] = await Promise.all([
        api.get("/admin/checks", { params: dirFilter ? { direction: dirFilter } : {} }),
        api.get("/admin/cari"),
      ]);
      setData(d);
      setCaris(cl);
    } catch { toast.error("Çek/senet listesi yüklenemedi"); }
  }, [dirFilter]);

  useEffect(() => { load(); }, [load]);

  const newForm = () => { setEditId(null); setForm({ direction: "alinan", doc_type: "cek", cari_id: "", bank: "", doc_no: "", amount: "", due_date: today, note: "" }); };
  const openEdit = (c) => { setEditId(c.check_id); setForm({ direction: c.direction, doc_type: c.doc_type, cari_id: c.cari_id || "", bank: c.bank || "", doc_no: c.doc_no || "", amount: c.amount, due_date: c.due_date, note: c.note || "" }); };

  const save = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast.error("Geçerli tutar girin"); return; }
    setBusy(true);
    try {
      if (editId) await api.put(`/admin/checks/${editId}`, { ...form, amount });
      else await api.post("/admin/checks", { ...form, amount });
      toast.success(editId ? "Kayıt güncellendi" : "Çek/senet kaydedildi");
      setForm(null); setEditId(null);
      load();
    } catch (e) { toast.error(errText(e) || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  const setStatus = async (c, status) => {
    try {
      await api.put(`/admin/checks/${c.check_id}/status`, { status });
      toast.success(`Durum: ${STATUSES.find(([v]) => v === status)?.[1]}${status === "odendi" && c.cari_id ? " — cari'ye işlendi" : ""}`);
      load();
    } catch (e) { toast.error(errText(e) || "Güncellenemedi"); }
  };

  const remove = async (c) => {
    if (!window.confirm("Kayıt silinsin mi?")) return;
    try {
      await api.delete(`/admin/checks/${c.check_id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(errText(e) || "Silinemedi"); }
  };

  const items = data?.items || [];
  const s = data?.summary;

  return (
    <div data-testid="admin-checks-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-forest">Çek & Senet Takibi</h1>
          <p className="text-stone-500 text-sm mt-1">Alınan ve verilen çek/senetler; ödendi işaretlenince cari'ye otomatik işlenir.</p>
        </div>
        <button data-testid="check-new-button" onClick={newForm} className="flex items-center gap-2 bg-forest hover:bg-forest-dark text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"><Plus className="w-4 h-4" /> Yeni Çek/Senet</button>
      </div>

      {s && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div data-testid="checks-alinan" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm"><p className="text-xs text-stone-500 uppercase tracking-wide">Alınanlar (Portföyde)</p><p className="text-2xl font-bold text-emerald-700 mt-1">{tl(s.alinan_pending)} ₺</p></div>
          <div data-testid="checks-verilen" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm"><p className="text-xs text-stone-500 uppercase tracking-wide">Verilenler (Portföyde)</p><p className="text-2xl font-bold text-red-600 mt-1">{tl(s.verilen_pending)} ₺</p></div>
          <div className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm"><p className="text-xs text-stone-500 uppercase tracking-wide">7 Gün İçinde Vadesi Gelen</p><p className="text-2xl font-bold text-stone-900 mt-1">{s.due_soon}</p></div>
        </div>
      )}

      <div className="flex gap-2">
        {[["", "Tümü"], ["alinan", "Alınanlar"], ["verilen", "Verilenler"]].map(([v, l]) => (
          <button key={v} data-testid={`check-filter-${v || "all"}`} onClick={() => setDirFilter(v)} className={`px-3.5 py-2 rounded-xl text-xs font-semibold transition-colors ${dirFilter === v ? "bg-forest text-white" : "bg-white border border-stone-200 text-stone-600 hover:bg-stone-50"}`}>{l}</button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-stone-200/80 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead><tr className="text-left text-xs uppercase font-bold tracking-wider text-stone-600 bg-[#EDE8DE] border-b border-stone-300">
            <th className="px-5 py-3">Tür</th><th className="px-5 py-3">Cari / Açıklama</th><th className="px-5 py-3">Banka / No</th><th className="px-5 py-3">Vade</th><th className="px-5 py-3 text-right">Tutar</th><th className="px-5 py-3">Durum</th><th className="px-5 py-3 text-right">İşlem</th>
          </tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.check_id} data-testid={`check-row-${c.check_id}`} className={`border-b border-stone-100 ${c.overdue ? "bg-red-50/50" : ""}`}>
                <td className="px-5 py-3"><span className="font-medium">{c.type_label}</span><span className={`block text-xs ${c.direction === "alinan" ? "text-emerald-700" : "text-red-600"}`}>{c.direction_label}</span></td>
                <td className="px-5 py-3 font-medium text-stone-900">{c.cari_name || "-"}<span className="block text-xs text-stone-400 font-normal">{c.note}</span></td>
                <td className="px-5 py-3 text-stone-600 text-xs">{c.bank || "-"}<span className="block font-mono">{c.doc_no}</span></td>
                <td className={`px-5 py-3 ${c.overdue ? "text-red-700 font-semibold" : "text-stone-600"}`}>{c.due_date}{c.overdue && <span className="block text-[10px]">VADESİ GEÇTİ</span>}</td>
                <td className="px-5 py-3 text-right font-semibold">{tl(c.amount)} ₺</td>
                <td className="px-5 py-3"><span className={`px-2 py-1 rounded-lg text-xs font-semibold ${STATUS_CLS[c.status]}`}>{c.status_label}</span></td>
                <td className="px-5 py-3 text-right">
                  <div className="flex justify-end gap-1.5 items-center">
                    {c.status === "portfoy" && (
                      <select data-testid={`check-status-${c.check_id}`} value="" onChange={(e) => e.target.value && setStatus(c, e.target.value)} className="h-8 px-2 rounded-lg border border-stone-300 text-xs bg-white">
                        <option value="">Durum...</option>
                        {STATUSES.filter(([v]) => v !== "portfoy").map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    )}
                    <button data-testid={`check-edit-${c.check_id}`} onClick={() => openEdit(c)} className="p-1.5 hover:bg-cream-dark rounded-lg" title="Düzenle"><Pencil className="w-4 h-4 text-stone-500" /></button>
                    <button data-testid={`check-delete-${c.check_id}`} onClick={() => remove(c)} className="p-1.5 hover:bg-red-50 rounded-lg" title="Sil (ödendiyse cari etkisi geri alınır)"><Trash2 className="w-4 h-4 text-red-400" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="7" className="px-5 py-10 text-center text-stone-400"><ScrollText className="w-8 h-8 mx-auto mb-2 opacity-40" />Kayıt bulunamadı</td></tr>}
          </tbody>
        </table>
      </div>

      {form && (
        <div data-testid="check-form-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-forest">{editId ? "Çek / Senet Düzenle" : "Yeni Çek / Senet"}</h3>
              <button data-testid="check-form-close" onClick={() => setForm(null)} className="p-1.5 hover:bg-stone-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold text-stone-600">Yön</label>
                <select data-testid="check-form-direction" className={inputCls} value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}>
                  <option value="alinan">Alınan (müşteriden)</option><option value="verilen">Verilen (tedarikçiye)</option>
                </select></div>
              <div><label className="text-xs font-semibold text-stone-600">Evrak</label>
                <select data-testid="check-form-type" className={inputCls} value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })}>
                  <option value="cek">Çek</option><option value="senet">Senet</option>
                </select></div>
              <div className="col-span-2"><label className="text-xs font-semibold text-stone-600">Cari (opsiyonel)</label>
                <select data-testid="check-form-cari" className={inputCls} value={form.cari_id} onChange={(e) => setForm({ ...form, cari_id: e.target.value })}>
                  <option value="">—</option>
                  {caris.map((c) => <option key={c.cari_id} value={c.cari_id}>{c.name}</option>)}
                </select></div>
              <div><label className="text-xs font-semibold text-stone-600">Banka</label><input data-testid="check-form-bank" className={inputCls} value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-stone-600">Evrak No</label><input className={inputCls} value={form.doc_no} onChange={(e) => setForm({ ...form, doc_no: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-stone-600">Tutar ₺ *</label><input data-testid="check-form-amount" type="number" min="0" step="0.01" className={inputCls} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-stone-600">Vade *</label><input data-testid="check-form-due" type="date" className={inputCls} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-xs font-semibold text-stone-600">Not</label><input className={inputCls} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
            </div>
            <button data-testid="check-form-submit" onClick={save} disabled={busy} className="mt-5 w-full bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white font-semibold py-3 rounded-xl transition-colors">{busy ? "Kaydediliyor..." : "Kaydet"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
